/**
 * Service Worker Initialization Module
 * Registers and manages service worker for PWA features
 */

const SW_PATH = '/sw.js';

/**
 * Initialize service worker registration
 */
export function initializeServiceWorker() {
    if (!isServiceWorkerSupported()) {
        console.log('Service Worker not supported in this browser');
        return;
    }

    window.addEventListener('load', registerServiceWorker);
}

/**
 * Check if service worker is supported
 * @returns {boolean} True if supported
 */
function isServiceWorkerSupported() {
    return 'serviceWorker' in navigator;
}

/**
 * Register service worker
 */
async function registerServiceWorker() {
    try {
        const registration = await navigator.serviceWorker.register(SW_PATH);
        console.log('✓ ServiceWorker registration successful:', registration.scope);

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
            handleServiceWorkerUpdate(registration);
        });

        // Check for updates periodically (every hour)
        setInterval(() => {
            registration.update();
        }, 60 * 60 * 1000);

    } catch (err) {
        console.error('ServiceWorker registration failed:', err);
    }
}

/**
 * Handle service worker update
 * @param {ServiceWorkerRegistration} registration - Service worker registration
 */
function handleServiceWorkerUpdate(registration) {
    const newWorker = registration.installing;
    if (!newWorker) return;

    newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker available
            notifyUserOfUpdate();
        }
    });
}

/**
 * Notify user of available update
 */
function notifyUserOfUpdate() {
    // Show update notification
    if (typeof window.showSuccessMessage === 'function') {
        window.showSuccessMessage('A new version is available! Refresh to update.');
    } else {
        console.log('Update available - please refresh the page');
    }
}

/**
 * Unregister service worker
 * @returns {Promise<boolean>} True if unregistered
 */
export async function unregisterServiceWorker() {
    if (!isServiceWorkerSupported()) {
        return false;
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
            const unregistered = await registration.unregister();
            console.log('Service Worker unregistered:', unregistered);
            return unregistered;
        }
        return false;
    } catch (err) {
        console.error('Service Worker unregistration failed:', err);
        return false;
    }
}

/**
 * Get service worker registration status
 * @returns {Promise<Object|null>} Registration object or null
 */
export async function getServiceWorkerRegistration() {
    if (!isServiceWorkerSupported()) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration();
        return registration || null;
    } catch (err) {
        console.error('Failed to get Service Worker registration:', err);
        return null;
    }
}

/**
 * Post message to service worker
 * @param {Object} message - Message to send
 */
export async function postMessageToServiceWorker(message) {
    if (!isServiceWorkerSupported()) {
        console.warn('Service Worker not supported');
        return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (registration && registration.active) {
        registration.active.postMessage(message);
    }
}

/**
 * Skip waiting service worker
 */
export async function skipWaitingServiceWorker() {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
}
